import { z } from 'zod';
import { defaultServer } from '~/api/def/servers/defaultServer';
import { getExecEndpoint } from '~/api/service/getExecEndpoint';
import { ApiEndpointDef } from '~/api/types';
import { BqResponse, CrudBqResponse, CrudResponseSuccessBase } from './types';

export const COMPANY_STATUS = ['live', 'disolved', 'writeOff'] as const;

export const INVESTMENT_ROUNDS = [
  'pre-seed',
  'seed',
  'series-a',
  'series-b',
  'series-c',
  'series-d',
  'write-off',
] as const;

export const portfolioCompanyCrudEndpoint = {
  server: defaultServer,
  path: 'crud/portfoliocompany',
  description: 'Portfolio Company operations',
};

export const portfolioCompanyLogoEndpoint = {
  server: defaultServer,
  path: 'portfoliocompany',
  description: 'Portfolio Company logo operations',
};

export const portfolioCompanyEndpoint = {
  server: defaultServer,
  path: 'portfoliocompany',
  description: 'Portfolio Company operations',
};

const endpointPortfolioCompanyCrudExec =
  getExecEndpoint<PortfolioCompanyCrudEndpointDef>(
    portfolioCompanyCrudEndpoint,
  );

export const endpointPortfolioCompanyByIdGET = async (id: string) => {
  const response = await endpointPortfolioCompanyCrudExec<'GET'>({
    method: 'GET',
    urlExtra: `/${id}`,
  });

  if (!response || !response.data) {
    throw new Error('Could not get portfolio company');
  }

  if (response.success === 0) {
    throw new Error('Could not get portfolio company');
  }

  return response.data;
};

export const endpointPortfolioCompanyPUT = async (
  id: string,
  payload: PortfolioCompanyPutPayload,
) => {
  return endpointPortfolioCompanyCrudExec<'PUT'>({
    method: 'PUT',
    payload,
    urlExtra: `/${id}`,
  });
};

export const endpointPortfolioCompanyPOST = async (
  payload: PortfolioCompanyPOSTPayload,
  withShareholders: boolean = false,
) => {
  const response = await endpointPortfolioCompanyCrudExec<'POST'>({
    method: 'POST',
    payload,
    urlExtra: withShareholders ? '/with-shareholders' : undefined,
  });

  if (!response || !response.data) {
    throw new Error('Could not create portfolio company');
  }

  return response;
};

const endpointPortfolioCompanyLogoExec =
  getExecEndpoint<PortfolioCompanyLogoEndpointDef>(
    portfolioCompanyLogoEndpoint,
  );

export const endpointPortfolioCompanyLogoPOST = async (
  id: string,
  payload: FormData,
) => {
  const response = await endpointPortfolioCompanyLogoExec<'POST'>({
    method: 'POST',
    payload,
    urlExtra: `/${id}/logo`,
    config: {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    },
  });

  if (!response) {
    throw new Error('Could not get portfolio company');
  }

  if (response.success === 0) {
    throw new Error('Could not get portfolio company');
  }

  return response;
};

const currentRound = z.string().optional();
const companyStatus = z.string().optional();

export const portfolioCompanyGetDataSchema = z.object({
  id: z.string().uuid(),
  name: z.string().trim().min(1, 'Name cannot be empty'),
  logo: z.string().nullable().optional(),
  incorporationDate: z.string().optional(),
  deletedAt: z.string().optional(),
  createdBy: z.string(),
  currentRound,
  companyStatus,
  createdAt: z.string(),
  updatedAt: z.string(),
  fundId: z.string().uuid(),
  sectorId: z.string().uuid(),
  companyId: z.string(),
  closeDate: z.string().nullable().optional(),
  shortName: z.string().trim().nullable().optional(),
  groupId: z.number().optional(),
  googleWatchChannel: z
    .object({
      folderId: z.string(),
      folderName: z.string(),
    })
    .optional(),
});

export type PortfolioCompanyGetData = z.infer<
  typeof portfolioCompanyGetDataSchema
>;

export const portfolioCompanyPutPayloadSchema = z.object({
  name: z.string().trim().min(1, 'Name cannot be empty'),
  incorporationDate: z.string(),
  currentRound,
  companyStatus,
  fundId: z.string().uuid(),
  sectorId: z.string().uuid(),
  jurisdictionId: z.string().uuid().nullable().optional(),
  closeDate: z.string().nullable().optional(),
  shortName: z.string().nullable().optional(),
  gDriveFolder: z.string().nullable().optional(),
});

export type PortfolioCompanyPutPayload = z.infer<
  typeof portfolioCompanyPutPayloadSchema
>;

export const portfolioCompanyPostPayloadSchema = z.object({
  name: z.string().trim().min(1, 'Name cannot be empty'),
  incorporationDate: z.string(),
  currentRound,
  companyStatus,
  jurisdictionId: z.string().uuid(),
  fundId: z.string().uuid(),
  sectorId: z.string().uuid(),
  closeDate: z.string().nullable().optional(),
  shortName: z.string().nullable().optional(),
  shareholders: z
    .array(
      z.object({
        name: z.string(),
        shareClass: z.string(),
        shareAmount: z.string(),
        nominalValue: z.string(),
        currency: z.string(),
      }),
    )
    .optional(),
});

export type PortfolioCompanyPOSTPayload = z.infer<
  typeof portfolioCompanyPostPayloadSchema
>;

export type PortfolioCompanyGetResponse =
  CrudBqResponse<PortfolioCompanyGetData>;

export interface PortfolioCompanyCrudEndpointDef {
  endpoint: typeof portfolioCompanyCrudEndpoint;
  methods: {
    GET: {
      response: PortfolioCompanyGetResponse;
    };
    PUT: {
      response: PortfolioCompanyPutResponse;
      payload: PortfolioCompanyPutPayload;
    };
    POST: {
      response: PortfolioCompanyPostResponse;
      payload: Omit<PortfolioCompanyPOSTPayload, 'id'>;
    };
  };
}

export type PortfolioCompanyLogoResponse = CrudBqResponse<{
  data: { logoFileKey: string };
}>;

export interface PortfolioCompanyLogoEndpointDef {
  endpoint: typeof portfolioCompanyCrudEndpoint;
  methods: {
    POST: {
      response: PortfolioCompanyLogoResponse;
      payload: FormData;
    };
  };
}

export type PortfolioCompanyPutResponse = CrudBqResponse<{ id: string }>;

export type PortfolioCompanyPostResponse = CrudBqResponse<{ id: string }>;

export function isPortfolioCompanyResponseOk(
  resp?: PortfolioCompanyPutResponse,
): resp is CrudResponseSuccessBase & {
  data: { id: string };
} {
  if (!resp) {
    return false;
  }

  if (resp?.success === 1) {
    return true;
  }

  return false;
}

export const endpointPortfolioCompanyExec = <
  TResponse extends ApiEndpointDef,
>() => getExecEndpoint<TResponse>(portfolioCompanyEndpoint);

export const endpointPortfolioCompanyGET = async (fundId?: string) => {
  const exec =
    endpointPortfolioCompanyExec<
      PortfolioCompanyEndpointDef<GetPortfolioCompanyResponse>
    >();

  const response = await exec<'GET'>({
    method: 'GET',
    urlExtra: fundId ? `?fundId=${fundId}` : undefined,
  });

  if (!response) {
    throw new Error('Could not valuation events');
  }

  if (response.success === 0) {
    throw new Error('Could not valuation events');
  }

  return response.data;
};

export const endpointPCompanyValuationEventGET = async (pCompanyid: string) => {
  const exec =
    endpointPortfolioCompanyExec<
      PortfolioCompanyEndpointDef<GetPcValuationEventResponse>
    >();

  const response = await exec<'GET'>({
    method: 'GET',
    urlExtra: `/${pCompanyid}/valuation-event`,
  });
  if (!response) {
    throw new Error('Could not valuation events');
  }

  if (response.success === 0) {
    throw new Error('Could not valuation events');
  }

  return response.data;
};

export const endpointCapTableGET = async (pCompanyid: string) => {
  const exec =
    endpointPortfolioCompanyExec<
      PortfolioCompanyEndpointDef<GetCapTableResponse>
    >();

  const response = await exec<'GET'>({
    method: 'GET',
    urlExtra: `/${pCompanyid}/cap-table`,
  });
  if (!response) {
    throw new Error('Could not get sectors');
  }

  if (response.success === 0) {
    throw new Error('Could not get sectors');
  }

  return response.data;
};

export const endpointPcAnalyticsGET = async (pCompanyid: string) => {
  const exec =
    endpointPortfolioCompanyExec<
      PortfolioCompanyEndpointDef<GetPcAnalyticsResponse>
    >();

  const response = await exec<'GET'>({
    method: 'GET',
    urlExtra: `/${pCompanyid}/analytics`,
  });
  if (!response) {
    throw new Error('Could not get analytics');
  }

  if (response.success === 0) {
    throw new Error('Could not get analytics');
  }

  return response.data;
};

export const endpointSearchPcCompaniesGET = async (
  fundId?: string,
  searchQuery?: string,
) => {
  const exec =
    endpointPortfolioCompanyExec<
      PortfolioCompanyEndpointDef<GetSearchPcCompaniesResponse>
    >();

  const response = await exec<'GET'>({
    method: 'GET',
    urlExtra:
      fundId || searchQuery
        ? `/search?${fundId ? `fundId=${fundId}` : ''}${
            fundId && searchQuery ? '&' : ''
          }${searchQuery ? `q=${searchQuery}` : ''}`
        : undefined,
  });
  if (!response) {
    throw new Error('Could not get portfolio companies');
  }

  if (response.success === 0) {
    throw new Error('Could not get portfolio companies');
  }

  return response.data;
};

export const endpointManualValuationPOST = async (
  pCompanyid: string,
  payload: ManualValuationEventPayload,
) => {
  const exec =
    endpointPortfolioCompanyExec<
      PortfolioCompanyEndpointDef<ManualValuationEventResponse>
    >();
  const response = await exec<'POST'>({
    method: 'POST',
    urlExtra: `/${pCompanyid}/manual-valuation-event`,
    payload,
  });
  if (!response) {
    throw new Error('Could not create manual valuation event.');
  }

  if (response.success === 0) {
    throw new Error('Could not create manual valuation event.');
  }

  return response.data;
};

export const endpointPcGoogleWatchChannelPUT = async (
  pCompanyid: string,
  payload: PortfolioCompanyGoogleWatchChannelSyncPayload,
) => {
  const exec =
    endpointPortfolioCompanyExec<
      PortfolioCompanyEndpointDef<PcGoogleDriveWatchChannelResponse>
    >();
  const response = await exec<'PUT'>({
    method: 'PUT',
    urlExtra: `/${pCompanyid}/g-drive-sync`,
    payload,
  });
  if (!response) {
    throw new Error('Could not create/update google drive sync.');
  }

  if (response.success === 0) {
    throw new Error('Could not create/update google drive sync.');
  }

  return response;
};

export interface PortfolioCompanyEndpointDef<TResponse> {
  endpoint: typeof portfolioCompanyEndpoint;
  methods: {
    POST: {
      payload: ManualValuationEventPayload;
      response: ManualValuationEventResponse;
    };
    GET: {
      response: TResponse;
    };
    PUT: {
      payload: PortfolioCompanyGoogleWatchChannelSyncPayload;
      response: TResponse;
    };
  };
}

type PortfolioCompanyGoogleWatchChannelSyncPayload = {
  folderIds: string[];
  fundId: string;
};

export type GetPortfolioCompanyResponse = BqResponse<{
  data: PortfolioCompanyResponse[];
}>;

export type PortfolioCompanyResponse = {
  id: string;
  name: string;
  shortName: string;
  incorporationDate: Date | string;
  currentRound: string;
  companyStatus: string;
  closeDate: Date | string;
  sectorName: string;
  pcGroupName: string;
  fundId: string;
  fundName: string;
  createdAt: Date | string;
  updatedAt: string;
  jurisdiction: {
    name: string;
    abbreviation: string;
  };
  pCValuations: {
    followOnByFund?: number;
    initialInvestmentByFund?: number;
    totalRaised?: number;
    ownership?: number;
  }[];
};

export type ValuationEventResponse = {
  id: string;
  valuationDate: Date | string;
  settlementDate: Date | string;
  adjustmentFactor: number;
  note: string;
  instrumentRound: number;
  round: string;
  currency: string;
  securityType: string;
  shareClass: string;
  schema: string;
  fullyDiluted: number;
  shares: number | string;
  pricePerShare: number;
  totalValue: number | string;
  recipient: string;
};

export type GetPcValuationEventResponse = BqResponse<{
  data: ValuationEventResponse[];
}>;

export type CapTableResponse = {
  investorId: string;
  shareholderName: string;
  shareholderType: string;
  shares: number;
  schema: string;
  currency: string;
  ownership: number;
  valuation: number;
  shareClass: string;
};

export type GetCapTableResponse = BqResponse<{
  data: CapTableResponse[];
}>;

export type PcAnalyticsResponse = {
  id: string;
  name: string;
  fundId: string;
  fundName: string;
  initialInvestment?: number;
  investedToDate?: number;
  currentStage: string;
  sector: string;
  geography: string;
  status: number;
  incorporationDate: Date;
  currentIrr: number;
  currentEsop: number;
  tvpi: string;
  unrealised: number;
  realised: number;
  netMoic: number;
  ownership: number;
  valuationOverTime: ValuationOverTime[];
  grossMoic: number;
  currency: string;
  currentPrice: number;
  currentFullyDiluted: number;
  noOfInvestors: number;
  totalFundShares: number;
  investors: Investor[];
};

export type Investor = {
  id: string;
  name: string;
  currency: string;
  type: string;
  valueLocal: number;
};

export type ValuationOverTime = {
  valuationDate: Date;
  currentValuation: number;
};

export type GetPcAnalyticsResponse = BqResponse<{
  data: PcAnalyticsResponse;
}>;

export type ManualValuationEventPayload = {
  type: string;
  value: number;
  note: string;
  currency: string;
  investorId: string;
};

export type ManualValuationEventResponse = BqResponse<{
  data: object;
}>;

export type GetSearchPcCompaniesResponse = BqResponse<{
  data: SearchPcCompaniesResponse[];
}>;

export type SearchPcCompaniesResponse = {
  id: string;
  name: string;
};

export type PcGoogleDriveWatchChannelResponse = BqResponse<{
  data: object;
}>;
